import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import styles from './Feedback.module.css'

export interface ToastAction {
  readonly label: string
  readonly onAction: () => void
}

export interface ToastOptions {
  readonly message: string
  readonly action?: ToastAction
  readonly tone?: 'neutral' | 'danger'
  /** Milliseconds before auto-dismissal. */
  readonly durationMs?: number
}

interface ActiveToast extends ToastOptions {
  readonly id: number
}

interface ToastApi {
  readonly show: (options: ToastOptions) => void
}

const ToastContext = createContext<ToastApi | null>(null)

/**
 * Toasts carrying an undo stay up longer.
 *
 * The default is a notification; the longer one is a decision. Four seconds is
 * not enough to notice a message, read it, and decide to reverse something —
 * and an undo that expires before it is seen is the same as no undo.
 */
const DEFAULT_MS = 4000
const WITH_ACTION_MS = 9000

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly ActiveToast[]>([])
  const nextId = useRef(0)

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const show = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++
      setToasts((current) => [...current, { ...options, id }])
      const duration =
        options.durationMs ?? (options.action ? WITH_ACTION_MS : DEFAULT_MS)
      setTimeout(() => dismiss(id), duration)
    },
    [dismiss],
  )

  const api = useMemo(() => ({ show }), [show])

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/*
        A permanently mounted live region, not one that appears with the first
        toast. Screen readers only announce changes inside a region that already
        existed when the change happened — a region added at the same moment as
        its content is silent.
      */}
      <div className={styles['toastRegion']} role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`${styles['toast']} ${toast.tone === 'danger' ? styles['toastDanger'] : ''}`}
          >
            <span className={styles['toastMessage']}>{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                className={styles['toastAction']}
                onClick={() => {
                  toast.action?.onAction()
                  dismiss(toast.id)
                }}
              >
                {toast.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext)
  if (!api) {
    throw new Error('useToast must be used inside a <ToastProvider>')
  }
  return api
}
